'use client';

import { Fragment, useMemo, useState } from 'react';
import BrokerCard from './BrokerCard';
import EmptyState from './EmptyState';
import {
  ACCOUNT_TYPE_OPTIONS,
  FOCUS_OPTIONS,
  INTEGRATION_OPTIONS,
  PRODUCT_OPTIONS,
  SORT_KEYS,
  type Broker,
  type FilterState,
  type SliderBounds,
  type SortKey,
} from './types';
import type { ToolsTranslations } from '@/lib/translations/types';

interface Props {
  brokers: Broker[];
  translations: ToolsTranslations['brokerComparison'];
}

export default function BrokerComparisonTool({ brokers, translations }: Props) {
  const t = translations;

  // Slider bounds derived from the actual data so the controls always span
  // the meaningful range. Memoized — brokers is a stable server-side prop.
  const depositBounds = useMemo(
    () => deriveBounds(brokers.map((b) => b.minDepositNumber)),
    [brokers],
  );
  const fxBounds = useMemo(
    () => deriveBounds(
      brokers.map((b) => b.exchangeFeePercent),
      { decimals: 2 },
    ),
    [brokers],
  );

  // Only surface focus options that actually appear in the data set.
  const availableFocus = useMemo(() => {
    const present = new Set(brokers.flatMap((b) => b.focusIds));
    return FOCUS_OPTIONS.filter((opt) => present.has(opt.id));
  }, [brokers]);

  const hasAutoTax = useMemo(() => brokers.some((b) => b.autoTaxReporting), [brokers]);
  const hasAnyOffer = useMemo(() => brokers.some((b) => b.hasOffer), [brokers]);
  const hasBankId = useMemo(() => brokers.some((b) => b.bankId), [brokers]);

  // Only surface integration options that actually exist in the data set,
  // mirroring the focus filter behavior.
  const availableIntegrations = useMemo(() => {
    const present = new Set(brokers.flatMap((b) => b.integrationIds));
    return INTEGRATION_OPTIONS.filter((opt) => present.has(opt.id));
  }, [brokers]);

  const [filters, setFilters] = useState<FilterState>({
    sortBy: 'default',
    maxDeposit: depositBounds.max,
    maxFx: fxBounds.max,
    accounts: [],
    products: [],
    focus: '',
    fiOnly: false,
    autoTaxOnly: false,
    hasOfferOnly: false,
    bankIdOnly: false,
    integrations: [],
  });

  const filtered = useMemo(() => {
    const passed = brokers.filter((b) => {
      if (b.minDepositNumber > filters.maxDeposit) return false;
      if (b.exchangeFeePercent > filters.maxFx) return false;
      if (filters.accounts.length && !filters.accounts.every((a) => b.accountTypes.includes(a))) return false;
      if (filters.products.length && !filters.products.every((p) => b.productTypes.includes(p))) return false;
      if (filters.focus && !b.focusIds.includes(filters.focus)) return false;
      if (filters.fiOnly && !b.fiLicensed) return false;
      if (filters.autoTaxOnly && !b.autoTaxReporting) return false;
      if (filters.hasOfferOnly && !b.hasOffer) return false;
      if (filters.bankIdOnly && !b.bankId) return false;
      if (filters.integrations.length && !filters.integrations.every((i) => b.integrationIds.includes(i))) return false;
      return true;
    });
    return sortBrokers(passed, filters.sortBy);
  }, [brokers, filters]);

  const toggleArrayValue = (key: 'accounts' | 'products' | 'integrations', value: string) =>
    setFilters((f) => ({
      ...f,
      [key]: f[key].includes(value) ? f[key].filter((x) => x !== value) : [...f[key], value],
    }));

  return (
    <section
      id="broker-comparison-tool"
      className="tools my-10 w-full max-w-[1400px] mx-auto overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-[0_4px_24px_rgb(0_0_0_/_0.04)]"
    >
      {/* Header */}
      <div className="border-b border-gray-100 bg-gradient-to-b from-blue-50 to-white px-6 py-7 sm:px-7">
        <div className="mb-2 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-blue-700">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
            <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
          </svg>
          {t.eyebrow}
        </div>
        <h2 className="m-0 text-2xl font-extrabold tracking-tight text-blue-900 sm:text-[26px]">
          {t.title}
        </h2>
      </div>

      {/* Body: sidebar + main */}
      <div className="grid min-h-[400px] grid-cols-1 lg:grid-cols-[240px_1fr]">
        {/* Sidebar */}
        <aside className="flex flex-col gap-6 border-b border-gray-100 bg-white p-5 lg:border-b-0 lg:border-r">
          <div className="text-[12px] font-bold uppercase tracking-wider text-blue-900 border-b border-blue-900/40 pb-3">
            Filter
          </div>
          {hasAnyOffer && (
            <OfferToggle
              checked={filters.hasOfferOnly}
              onChange={() => setFilters((f) => ({ ...f, hasOfferOnly: !f.hasOfferOnly }))}
              label={t.hasOfferOnly}
            />
          )}

          <FilterBlock title={t.filters.sortBy}>
            <SortSelect
              value={filters.sortBy}
              onChange={(v) => setFilters((f) => ({ ...f, sortBy: v }))}
              labels={t.sortOptions}
            />
          </FilterBlock>

          {availableFocus.length > 0 && (
            <FilterBlock title={t.filters.focus}>
              <FocusSelect
                value={filters.focus}
                onChange={(v) => setFilters((f) => ({ ...f, focus: v }))}
                options={availableFocus}
                labels={t.focusOptions}
              />
            </FilterBlock>
          )}

          {!depositBounds.degenerate && (
            <FilterBlock title={t.filters.maxDeposit}>
              <Slider
                min={depositBounds.min}
                max={depositBounds.max}
                step={depositBounds.step}
                value={filters.maxDeposit}
                onChange={(v) => setFilters((f) => ({ ...f, maxDeposit: v }))}
                format={(v) =>
                  v === 0
                    ? t.depositOnlyZero
                    : `≤ ${formatNumber(v)} ${t.currencySuffix}${v >= depositBounds.max ? ' +' : ''}`
                }
              />
            </FilterBlock>
          )}

          {!fxBounds.degenerate && (
            <FilterBlock title={t.filters.maxFx}>
              <Slider
                min={fxBounds.min}
                max={fxBounds.max}
                step={fxBounds.step}
                value={filters.maxFx}
                onChange={(v) => setFilters((f) => ({ ...f, maxFx: v }))}
                format={(v) => `≤ ${v.toFixed(2)} %`}
              />
            </FilterBlock>
          )}

          <FilterBlock title={t.filters.accountType}>
            {ACCOUNT_TYPE_OPTIONS.map((opt) => (
              <CheckRow
                key={opt.id}
                checked={filters.accounts.includes(opt.id)}
                onChange={() => toggleArrayValue('accounts', opt.id)}
              >
                {opt.label}
              </CheckRow>
            ))}
          </FilterBlock>

          <FilterBlock title={t.filters.products}>
            {PRODUCT_OPTIONS.map((opt) => (
              <CheckRow
                key={opt.id}
                checked={filters.products.includes(opt.id)}
                onChange={() => toggleArrayValue('products', opt.id)}
              >
                <span className="capitalize">{opt.label}</span>
              </CheckRow>
            ))}
          </FilterBlock>

          {availableIntegrations.length > 0 && (
            <FilterBlock title={t.filters.integrations}>
              {availableIntegrations.map((opt) => (
                <CheckRow
                  key={opt.id}
                  checked={filters.integrations.includes(opt.id)}
                  onChange={() => toggleArrayValue('integrations', opt.id)}
                >
                  {opt.label}
                </CheckRow>
              ))}
            </FilterBlock>
          )}

          <FilterBlock title={t.filters.requirements}>
            <CheckRow
              checked={filters.fiOnly}
              onChange={() => setFilters((f) => ({ ...f, fiOnly: !f.fiOnly }))}
            >
              {t.fiOnly}
            </CheckRow>
            {hasAutoTax && (
              <CheckRow
                checked={filters.autoTaxOnly}
                onChange={() => setFilters((f) => ({ ...f, autoTaxOnly: !f.autoTaxOnly }))}
              >
                {t.autoTaxOnly}
              </CheckRow>
            )}
            {hasBankId && (
              <CheckRow
                checked={filters.bankIdOnly}
                onChange={() => setFilters((f) => ({ ...f, bankIdOnly: !f.bankIdOnly }))}
              >
                {t.bankIdOnly}
              </CheckRow>
            )}
          </FilterBlock>
        </aside>

        {/* Main */}
        <div className="bg-blue-50/15 p-5">
          <div className="mb-4 border-b border-gray-100 pb-3.5 text-[13px] text-gray-500">
            {t.showing}{' '}
            <strong className="text-gray-900">{filtered.length}</strong>{' '}
            {t.of} {brokers.length} {t.brokers}
          </div>

          <div className="flex flex-col gap-8">
            {brokers.length === 0 ? (
              <EmptyState title={t.empty.unavailableTitle} subtitle={t.empty.unavailableSubtitle} />
            ) : filtered.length === 0 ? (
              <EmptyState title={t.empty.noMatchTitle} subtitle={t.empty.noMatchSubtitle} />
            ) : (
              filtered.map((b, i) => (
                <Fragment key={b.id}>
                  {i > 0 && <CardDivider />}
                  <BrokerCard broker={b} translations={translations} />
                </Fragment>
              ))
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

// ---------- Sorting ----------

function sortBrokers(brokers: Broker[], key: SortKey): Broker[] {
  const sorted = [...brokers];
  switch (key) {
    case 'default':
      // "Recommended" — order by our review-driven rating. Brokers
      // without a rating sink to the bottom so we never lead with an
      // un-reviewed broker.
      sorted.sort((a, b) => {
        const aHas = a.appRatingNumber > 0 ? 1 : 0;
        const bHas = b.appRatingNumber > 0 ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
        return b.appRatingNumber - a.appRatingNumber;
      });
      break;
    case 'lowestCourtage':
      sorted.sort((a, b) => a.courtageTierRank - b.courtageTierRank);
      break;
    case 'lowestDeposit':
      sorted.sort((a, b) => a.minDepositNumber - b.minDepositNumber);
      break;
    case 'lowestFx':
      sorted.sort((a, b) => a.exchangeFeePercent - b.exchangeFeePercent);
      break;
    case 'highestRating':
      sorted.sort((a, b) => b.appRatingNumber - a.appRatingNumber);
      break;
    case 'highestSavings':
      // Brokers without a savings rate sink to the bottom.
      sorted.sort((a, b) => {
        const aHas = a.hasSavingsInterest ? 1 : 0;
        const bHas = b.hasSavingsInterest ? 1 : 0;
        if (aHas !== bHas) return bHas - aHas;
        return b.savingsInterestPercent - a.savingsInterestPercent;
      });
      break;
  }
  return sorted;
}

// ---------- Bounds derivation ----------

interface DeriveOptions {
  /** Number of decimals to keep when rounding step / max for display. */
  decimals?: number;
}

/**
 * Builds slider bounds (min/max/step) that span the actual data set.
 * - Min is floored to 0 when the lowest value is non-negative (always true here).
 * - Max is rounded up to a "nice" multiple of the chosen step so the slider
 *   ends on a clean number.
 * - Step is chosen to give roughly 10 segments across the range.
 */
function deriveBounds(values: number[], opts: DeriveOptions = {}): SliderBounds {
  if (values.length === 0) {
    return { min: 0, max: 0, step: 1, degenerate: true };
  }
  const minRaw = Math.min(...values);
  const maxRaw = Math.max(...values);
  if (minRaw === maxRaw) {
    return { min: minRaw, max: maxRaw, step: 1, degenerate: true };
  }

  const min = Math.min(0, minRaw);
  const range = maxRaw - min;
  const step = niceStep(range / 10);
  const max = Math.ceil(maxRaw / step) * step;
  const decimals = opts.decimals ?? (step >= 1 ? 0 : 2);
  return {
    min,
    max: round(max, decimals),
    step: round(step, decimals),
    degenerate: false,
  };
}

/**
 * Snaps an arbitrary value to the nearest "1 / 2 / 5 × 10^n" step.
 * Classic Tufte-style nice-number algorithm.
 */
function niceStep(raw: number): number {
  if (raw <= 0) return 1;
  const exp = Math.pow(10, Math.floor(Math.log10(raw)));
  const fraction = raw / exp;
  let nice;
  if (fraction <= 1) nice = 1;
  else if (fraction <= 2) nice = 2;
  else if (fraction <= 5) nice = 5;
  else nice = 10;
  return nice * exp;
}

function round(v: number, decimals: number): number {
  const f = Math.pow(10, decimals);
  return Math.round(v * f) / f;
}

function formatNumber(v: number): string {
  // Use a thin space as thousand separator; works in all our supported locales.
  return Math.round(v).toLocaleString('sv-SE').replace(/\u00A0/g, ' ');
}

// ---------- Sidebar primitives ----------

function OfferToggle({
  checked,
  onChange,
  label,
}: {
  checked: boolean;
  onChange: () => void;
  label: string;
}) {
  // Standalone primary filter — single pill, styled to echo the orange offer
  // pill on the broker cards. Active = filled orange, inactive = outline.
  return (
    <button
      type="button"
      onClick={onChange}
      aria-pressed={checked}
      className={`flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left text-[13px] font-semibold shadow-sm transition-colors ${
        checked
          ? 'border-orange-500 bg-orange-500 text-white'
          : 'border-orange-300 bg-white text-orange-700 hover:bg-orange-50'
      }`}
    >
      <svg className="shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z" />
        <line x1="7" y1="7" x2="7.01" y2="7" />
      </svg>
      <span className="truncate">{label}</span>
    </button>
  );
}

function FilterBlock({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-blue-900">
        {title}
      </div>
      {children}
    </div>
  );
}

function SortSelect({
  value,
  onChange,
  labels,
}: {
  value: SortKey;
  onChange: (v: SortKey) => void;
  labels: ToolsTranslations['brokerComparison']['sortOptions'];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value as SortKey)}
      className="w-full cursor-pointer rounded-md border border-gray-200 bg-white px-2.5 py-2 text-[13px] font-medium text-gray-800 shadow-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    >
      {SORT_KEYS.map((key) => (
        <option key={key} value={key}>
          {labels[key]}
        </option>
      ))}
    </select>
  );
}

function FocusSelect({
  value,
  onChange,
  options,
  labels,
}: {
  value: string;
  onChange: (v: string) => void;
  options: ReadonlyArray<{ id: string }>;
  labels: ToolsTranslations['brokerComparison']['focusOptions'];
}) {
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="w-full cursor-pointer rounded-md border border-gray-200 bg-white px-2.5 py-2 text-[13px] font-medium text-gray-800 shadow-sm outline-none transition-colors focus:border-blue-500 focus:ring-2 focus:ring-blue-100"
    >
      <option value="">{labels.all}</option>
      {options.map((opt) => (
        <option key={opt.id} value={opt.id}>
          {labels[opt.id as keyof typeof labels]}
        </option>
      ))}
    </select>
  );
}

function CheckRow({
  checked,
  onChange,
  children,
}: {
  checked: boolean;
  onChange: () => void;
  children: React.ReactNode;
}) {
  return (
    <label className="flex cursor-pointer items-center justify-between gap-2.5 py-1.5 text-[13px] text-gray-700">
      <span className="flex items-center gap-2.5">
        <span
          className={`flex h-4 w-4 shrink-0 items-center justify-center rounded ${
            checked ? 'bg-blue-700' : 'border-[1.5px] border-gray-300 bg-transparent'
          }`}
        >
          {checked && (
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3.5" aria-hidden="true">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          )}
        </span>
        <input
          type="checkbox"
          checked={checked}
          onChange={onChange}
          className="hidden"
          aria-label={typeof children === 'string' ? children : undefined}
        />
        {typeof children === 'string' ? <span>{children}</span> : children}
      </span>
    </label>
  );
}

function Slider({
  min,
  max,
  step,
  value,
  onChange,
  format,
}: {
  min: number;
  max: number;
  step: number;
  value: number;
  onChange: (v: number) => void;
  format: (v: number) => string;
}) {
  const clamped = Math.min(Math.max(value, min), max);
  const pct = max === min ? 100 : ((clamped - min) / (max - min)) * 100;
  return (
    <div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={clamped}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="h-1.5 w-full cursor-pointer appearance-none rounded-full outline-none"
        style={{
          background: `linear-gradient(90deg, #1d4ed8 0%, #1d4ed8 ${pct}%, #e5e7eb ${pct}%, #e5e7eb 100%)`,
          WebkitAppearance: 'none',
        }}
      />
      <div className="mt-1.5 text-[13px] font-semibold text-blue-700">{format(clamped)}</div>
    </div>
  );
}

function CardDivider() {
  // Decorative tri-dot divider sitting between consecutive broker cards.
  // Brand-leaning palette: blue / orange / green to echo the badges used
  // throughout the cards (FI-license green, focus blue, offer orange).
  return (
    <div className="flex items-center justify-center gap-2.5" aria-hidden="true">
      <span className="h-2 w-2 rounded-full bg-blue-400" />
      <span className="h-2 w-2 rounded-full bg-orange-400" />
      <span className="h-2 w-2 rounded-full bg-green-400" />
    </div>
  );
}

